<template>
  <div>
    <!-- Main Topic -->
    <div>
      <label>Main topic</label>
      <BaseTextarea
        type="text"
        placeholder="es. vacanze in val di rabbi"
        v-model="form.main_topic"
      />
    </div>

    <!-- Rubrica | Social Media Channel -->
    <div class="row mt-2">
      <div class="col-6">
        <label>Rubrica</label>
        <el-select
          :popper-append-to-body="false"
          class="select-primary w-100"
          size="medium"
          placeholder="Seleziona rubrica"
          v-model="form.rubrica"
        >
          <el-option
            v-for="(option, index) in rubricaOptions"
            class="select-primary"
            :value="option.value"
            :label="option.name"
            :key="index"
          />
        </el-select>
      </div>
      <div class="col-6">
        <label>Social Media Channel</label>
        <el-select
          :popper-append-to-body="false"
          class="select-primary w-100"
          size="medium"
          multiple
          placeholder="Seleziona canali"
          v-model="form.social_media_channel"
        >
          <el-option
            v-for="(channel, index) in channelOptions"
            class="select-primary"
            :value="channel.value"
            :label="channel.name"
            :key="index"
          />
        </el-select>
      </div>
    </div>

    <!-- Communication Goal | Query Keywords -->
    <div class="row mt-2">
      <div class="col-6">
        <label>Communication Goal</label>
        <el-select
          :popper-append-to-body="false"
          class="select-primary w-100"
          size="medium"
          placeholder="Seleziona communication goal"
          v-model="form.communication_goal"
        >
          <el-option
            v-for="(option, index) in goalOptions"
            class="select-primary"
            :value="option.value"
            :label="option.name"
            :key="index"
          />
        </el-select>
      </div>
      <div class="col-6">
        <label>Query Keywords</label>
        <div class="input_with_btn">
          <BaseInput
            class="flex-grow-1"
            type="text"
            :placeholder="'es. lago di tovel, trekking, brenta'"
            v-model="newKeyword"
            @keyup.enter="addKeyword">
          </BaseInput>
          <button
            class="btn__search" type="button"
            @click="addKeyword"
            :disabled="!newKeyword.trim()"
          >
            <i class="fa-solid fa-plus"></i>
          </button>
        </div>
        <div class="mt-2" v-if="form.query_keywords.length">
          <div class="bulk-file-list d-flex flex-wrap">
            <div class="seo_article__keyword mr-2 mb-2" v-for="(keyword,index) in form.query_keywords" :key="index">
              <p class="mb-0 seo_article__p">{{ keyword }} <i @click.prevent="removeKeyword(keyword)"
                 class="fa-regular fa-xmark seo_article__asterisk"></i> </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Emoji -->
    <div class="row mt-2">
      <div class="col">
        <div class="d-flex align-items-center">
          <label class="m-0 mr-3">Include Emoji</label>
          <el-switch
            v-model="form.include_emoji"
            active-text="Yes"
            inactive-text="No"
          />
        </div>
      </div>
    </div>

    <!-- Lingua | Brand Voice -->
    <div class="row mt-4">
      <div class="col-6">
        <label>Lingua</label>
        <el-select
          :popper-append-to-body="false"
          class="select-primary w-100"
          size="medium"
          v-model="form.language"
        >
          <el-option value="Italiano" label="Italiano" />
          <el-option value="English" label="English" />
        </el-select>
      </div>
      <div class="col-6">
        <BrandVoicesSelect
          :brand-voice-selected="form.brand_voice_id || null"
          @change="form.brand_voice_id = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { BaseInput, BaseTextarea } from 'src/components/index';
import { Select, Option, Switch } from 'element-ui';
import BrandVoicesSelect from 'src/pages/Tools/ToolsComponent/BrandVoicesSelect.vue';
import copySocialData from '@/jsonFiles/copySocialData.json';

export default {
  name: 'MicrocopySocialInputsForm',
  components: {
    BaseInput,
    BaseTextarea,
    [Option.name]: Option,
    [Select.name]: Select,
    [Switch.name]: Switch,
    BrandVoicesSelect,
  },
  props: {
    jsonData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: {
        main_topic: this.jsonData?.main_topic ?? '',
        rubrica: this.jsonData?.rubrica ?? '',
        communication_goal: this.jsonData?.communication_goal ?? '',
        social_media_channel: this.jsonData?.social_media_channel ?? [],
        query_keywords: this.jsonData?.query_keywords ?? [],
        include_emoji: this.jsonData?.include_emoji ?? false,
        language: this.jsonData?.language ?? 'Italiano',
        languages: this.jsonData?.languages ?? 'it',
        translate_from: this.jsonData?.translate_from ?? 'it',
        brand_voice_id: this.jsonData?.brand_voice_id ?? '',
      },
      goalOptions: [
        { value: 'inspirational', name: 'Inspirational' },
        { value: 'informativo', name: 'Informativo' },
        { value: 'hype', name: 'Hype' },
        { value: 'community_building', name: 'Community Building' },
        { value: 'emotional_valoriale', name: 'Emotional/valoriale' },
        { value: 'istitutional_corporate', name: 'Istitutional/Corporate' },
      ],
      rubricaOptions: [
        { value: 'itinerari', name: 'Itinerari' },
        { value: 'natural_wonders', name: 'Natural Wonders' },
        { value: 'curiosita', name: 'Curiosità' },
        { value: 'our_history', name: 'Our History' },
        { value: 'sport', name: 'Sport' },
        { value: 'best_of_the_month', name: 'Best of the Month' },
        { value: 'ask_the_guide', name: 'Ask the Guide' },
        { value: 'storie_vita_in_trentino', name: 'Storie Vita in Trentino' },
        { value: 'te_lo_ricordi', name: 'Te lo Ricordi' },
      ],
      channelOptions: copySocialData.channel,
      newKeyword: '',
    };
  },
  methods: {
    addKeyword() {
      const trimmedKeyword = this.newKeyword.trim();
      if (trimmedKeyword && !this.form.query_keywords.includes(trimmedKeyword)) {
        this.form.query_keywords.push(trimmedKeyword);
        this.newKeyword = '';
      }
    },
    removeKeyword(keywordToRemove) {
      this.form.query_keywords = this.form.query_keywords.filter(
        (keyword) => keyword !== keywordToRemove
      );
    },
  },
  watch: {
    jsonData: {
      handler(newVal) {
        if (!newVal) return;
        this.form.main_topic = newVal.main_topic ?? '';
        this.form.rubrica = newVal.rubrica ?? '';
        this.form.communication_goal = newVal.communication_goal ?? '';
        this.form.social_media_channel = newVal.social_media_channel ?? [];
        this.form.query_keywords = newVal.query_keywords ?? [];
        this.form.include_emoji = newVal.include_emoji ?? false;
        this.form.language = newVal.language ?? 'Italiano';
        this.form.languages = newVal.languages ?? 'it';
        this.form.translate_from = newVal.translate_from ?? 'it';
        this.form.brand_voice_id = newVal.brand_voice_id ?? '';
      },
      deep: true,
    },
    form: {
      handler() {
        this.$emit('update:formData', { ...this.form });
      },
      deep: true,
    },
  },
  mounted() {
    this.$emit('update:formData', { ...this.form });
  },
};
</script>
